import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Empty, Icon, Panel, type IconName } from '../design/index.js'
import { useApi, type SearchHit } from '../app/api.js'
import { Link } from '../app/Link.js'
import { navigate, paths } from '../app/router.js'
import { FormField } from '../framework/controls.js'
import { ErrorBlock, Loading, NoticeBlock } from '../framework/feedback.js'

const SEARCH_FIELD = {
  key: 'q', label: 'Type to search', control: 'text' as const,
  hint: 'A document number, a customer, a supplier, a material — or the name of a screen.',
}

/** Where one hit opens. A record opens itself; a type opens its list. */
const hitPath = (companyId: string, hit: SearchHit): string => {
  switch (hit.kind) {
    case 'document': return paths.document(companyId, hit.route, hit.id)
    case 'master': return paths.masterRecord(companyId, hit.route, hit.id)
    case 'type': return paths.list(companyId, hit.route)
  }
}

/** Morning, afternoon or evening, by the clock of whoever is reading. */
const partOfDay = (now: Date): string => {
  const h = now.getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

const TODAY = new Intl.DateTimeFormat('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

interface StatProps {
  readonly to: string
  readonly icon: IconName
  readonly title: string
  readonly caption: string
  readonly value: number
  readonly chip: string
  readonly tone: 'good' | 'warn' | 'bad' | 'none'
}

const Stat = ({ to, icon, title, caption, value, chip, tone }: StatProps) => (
  <Link to={to} className="stat">
    <span className="stat__head">
      <span className="stat__icon"><Icon name={icon} size={18} /></span>
      <span className="stat__title">{title}</span>
    </span>
    <span className="stat__caption">{caption}</span>
    <span className="stat__figure">{value > 999 ? '999+' : value}</span>
    <span className={tone === 'none' ? 'stat__chip' : `stat__chip stat__chip--${tone}`}>{chip}</span>
  </Link>
)

export const Home = ({ companyId }: { companyId: string }) => {
  const api = useApi()
  const home = useQuery({ queryKey: ['home', companyId], queryFn: () => api.home() })
  // The same query the sidebar badges use, so this is a cache hit, not a call.
  const inbox = useQuery({
    queryKey: ['inboxCounts', companyId],
    queryFn: () => api.inboxCounts(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  })
  const [term, setTerm] = useState('')
  const [needle, setNeedle] = useState('')

  /*
    Records, not only the names of screens.

    The box used to filter the document-type menu in the browser, so typing a
    project number or an invoice number found nothing at all — while the label
    said "Type to search" and docs/01 describes a global menu-and-entity
    search. The server answers now, over what this person is allowed to see.

    Debounced, because this is a keystroke-by-keystroke box and each answer is
    a handful of queries.
  */
  useEffect(() => {
    const trimmed = term.trim()
    const timer = setTimeout(() => setNeedle(trimmed), 200)
    return () => clearTimeout(timer)
  }, [term])

  const search = useQuery({
    queryKey: ['search', companyId, needle],
    queryFn: () => api.search(needle),
    enabled: needle.length >= 2,
    staleTime: 30_000,
  })

  // Grouped in the order the server sent them, so documents come before the
  // screens named after them.
  const groups = useMemo(() => {
    const out = new Map<string, SearchHit[]>()
    for (const hit of search.data ?? []) {
      const list = out.get(hit.group)
      if (list) list.push(hit)
      else out.set(hit.group, [hit])
    }
    return [...out.entries()]
  }, [search.data])

  if (home.isLoading) return <div className="page"><Loading /></div>
  if (home.error) return <div className="page"><ErrorBlock error={home.error} /></div>

  const payload = home.data
  const unread = payload?.unreadNotifications ?? 0
  const counts = inbox.data ?? { approvals: 0, tasks: 0, alerts: 0 }
  const favourites = payload?.favourites ?? []
  const now = new Date()

  return (
    <div className="page">
      <div className="doc-head">
        <div>
          <h1 className="greeting">{partOfDay(now)}, {payload?.greetingName ?? 'there'}</h1>
          <p className="greeting-date">
            {TODAY.format(now)} · {payload?.company.name ?? 'This company'}
          </p>
        </div>
        <div className="doc-actions">
          <Link to={paths.hub(companyId, 'reports')} className="btn btn--ghost">
            <span className="btn__icon"><Icon name="chart" size={18} /></span>
            Reports
          </Link>
          <Link to={paths.hub(companyId, 'transactions')} className="btn btn--primary">
            <span className="btn__icon"><Icon name="plus" size={18} /></span>
            New document
          </Link>
        </div>
      </div>

      <div className="stat-grid">
        <Stat
          to={paths.hub(companyId, 'approvals')}
          icon="check"
          title="Approvals"
          caption="Waiting for your signature"
          value={counts.approvals}
          chip={counts.approvals > 0 ? 'Waiting for you' : 'Nothing waiting'}
          tone={counts.approvals > 0 ? 'warn' : 'good'}
        />
        <Stat
          to={paths.hub(companyId, 'tasks')}
          icon="clip"
          title="Tasks"
          caption="Work assigned to you"
          value={counts.tasks}
          chip={counts.tasks > 0 ? 'Open' : 'All done'}
          tone={counts.tasks > 0 ? 'none' : 'good'}
        />
        <Stat
          to={paths.hub(companyId, 'alerts')}
          icon="bell"
          title="Alerts"
          caption="Notifications you have not read"
          value={Math.max(counts.alerts, unread)}
          chip={Math.max(counts.alerts, unread) > 0 ? 'Unread' : 'All read'}
          tone={Math.max(counts.alerts, unread) > 0 ? 'bad' : 'good'}
        />
        <Stat
          to={paths.hub(companyId, 'favourites')}
          icon="star"
          title="Starred"
          caption="Screens you keep close"
          value={favourites.length}
          chip={favourites.length > 0 ? 'Shortcuts below' : 'Star a screen to add one'}
          tone="none"
        />
      </div>

      <div className="home-grid">
        <Panel title="Search">
          <FormField field={SEARCH_FIELD} value={term} onChange={(v) => setTerm(typeof v === 'string' ? v : '')} />
          {groups.length > 0 ? (
            <div className="search-results">
              {groups.map(([group, hits]) => (
                <div key={group}>
                  <p className="search-results__heading">{group}</p>
                  {hits.map((hit) => (
                    <button
                      key={`${hit.kind}:${hit.id}`}
                      type="button"
                      className="search-result"
                      onClick={() => navigate(hitPath(companyId, hit))}
                    >
                      {hit.label}
                      {hit.secondary ? <span className="search-result__group"> · {hit.secondary}</span> : null}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ) : needle.length >= 2 && !search.isFetching ? (
            <p className="helper" style={{ marginTop: 12 }}>No results found.</p>
          ) : null}
        </Panel>

        <Panel title="Starred shortcuts" aside={favourites.length > 0 ? `${favourites.length}` : undefined}>
          {favourites.length === 0 ? (
            <Empty
              title="Nothing starred yet."
              body="Star an item on the Transactions, Reports or Masters page and it will be listed here."
            />
          ) : (
            <div>
              {favourites.map((f) => (
                <Link key={f.key} to={paths.list(companyId, f.route)} className="shortcut">
                  <span>{f.label}</span>
                  <span className="helper" style={{ marginLeft: 'auto' }}>{f.hub}</span>
                </Link>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  )
}
